<template>
  <div
    class="carrossel-step-revisao"
    :class="{ 'carrossel-step-revisao--dark': darkEnabled }"
  >
    <h3 class="step-title">Revisão</h3>
    <p class="step-subtitle">
      Confira todas as configurações antes de publicar o {{ entityLabel }}.
    </p>
    <p v-if="!wizardState.carrossel.ativo" class="review-publish-hint">
      Ao finalizar, o {{ entityLabel }} será publicado como ativo. Status atual:
      {{ summary.status }}.
    </p>

    <v-divider class="mb-5" />

    <div class="review-sections">
      <!-- Informações básicas -->
      <section class="review-card">
        <header class="review-card__header">
          <div class="review-card__heading">
            <span class="review-card__icon-wrap">
              <v-icon small>fas fa-info-circle</v-icon>
            </span>
            <h4 class="review-card__title">Informações básicas</h4>
          </div>
          <FormButton
            text="Editar etapa"
            type="secundario"
            outlined
            before-icon="fas fa-pencil-alt"
            @click="goToStep(1)"
          />
        </header>
        <div class="review-fields">
          <div class="review-field">
            <span class="review-field__label">Nome do {{ entityLabel }}</span>
            <span class="review-field__value">{{
              summary.informacoes.titulo
            }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Local de exibição</span>
            <span class="review-pill">{{ summary.informacoes.local }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Período de vigência</span>
            <span class="review-field__value">{{
              summary.informacoes.vigencia
            }}</span>
          </div>
          <div v-if="summary.informacoes.paginasEstaticas" class="review-field">
            <span class="review-field__label">Páginas estáticas</span>
            <span class="review-pill review-pill--muted">
              {{ summary.informacoes.paginasEstaticas }}
            </span>
          </div>
        </div>
      </section>

      <!-- Conteúdo (vitrine) -->
      <section v-if="productEditorMode && summary.conteudo" class="review-card">
        <header class="review-card__header">
          <div class="review-card__heading">
            <span class="review-card__icon-wrap">
              <v-icon small>fas fa-box-open</v-icon>
            </span>
            <h4 class="review-card__title">Conteúdo</h4>
          </div>
          <FormButton
            text="Editar etapa"
            type="secundario"
            outlined
            before-icon="fas fa-pencil-alt"
            @click="goToStep(2)"
          />
        </header>
        <div class="review-fields">
          <div class="review-field">
            <span class="review-field__label">Tipo de vitrine</span>
            <span class="review-field__value">{{ summary.conteudo.tipo }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Categoria de produtos</span>
            <span class="review-pill review-pill--muted">
              {{ summary.conteudo.categoria }}
            </span>
          </div>
        </div>
      </section>

      <!-- Banners -->
      <section v-if="!productEditorMode" class="review-card">
        <header class="review-card__header">
          <div class="review-card__heading">
            <span class="review-card__icon-wrap">
              <v-icon small>fas fa-images</v-icon>
            </span>
            <h4 class="review-card__title">Banners</h4>
          </div>
          <FormButton
            text="Editar etapa"
            type="secundario"
            outlined
            before-icon="fas fa-pencil-alt"
            @click="goToStep(2)"
          />
        </header>
        <div class="review-fields">
          <div class="review-field">
            <span class="review-field__label">Quantidade de banners</span>
            <span class="review-field__value">{{ summary.banners.count }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Ações configuradas</span>
            <span
              class="review-pill"
              :class="{
                'review-pill--success': bannersActionsLabel !== 'Sem ação'
              }"
            >
              {{ bannersActionsLabel }}
            </span>
          </div>
          <div
            v-if="summary.banners.images.length"
            class="review-field review-field--thumbs"
          >
            <span class="review-field__label">Pré-visualização</span>
            <div class="review-thumbs">
              <img
                v-for="(url, index) in summary.banners.images"
                :key="index"
                :src="url"
                alt=""
                class="review-thumb"
              />
            </div>
          </div>
          <div
            v-if="summary.banners.programacoes && summary.banners.programacoes.length"
            class="review-field review-field--programacao"
          >
            <span class="review-field__label">Programação de exibição</span>
            <ul class="review-programacao">
              <li
                v-for="banner in summary.banners.programacoes"
                :key="banner.indice"
              >
                <span class="review-programacao__banner">{{
                  banner.label
                }}</span>
                <span class="review-programacao__value">{{
                  banner.programacao
                }}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Público -->
      <section class="review-card">
        <header class="review-card__header">
          <div class="review-card__heading">
            <span class="review-card__icon-wrap">
              <v-icon small>fas fa-users</v-icon>
            </span>
            <h4 class="review-card__title">Público</h4>
          </div>
          <FormButton
            text="Editar etapa"
            type="secundario"
            outlined
            before-icon="fas fa-pencil-alt"
            @click="goToStep(3)"
          />
        </header>
        <div class="review-fields">
          <div class="review-field">
            <span class="review-field__label">Segmento de clientes</span>
            <span class="review-pill review-pill--muted">
              {{ summary.publico.segmento }}
            </span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Exibir para usuários</span>
            <span class="review-field__value">{{
              summary.publico.userLoginType
            }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Com pesquisa ativada</span>
            <span class="review-field__value">
              {{ summary.publico.showOnPesquisaAtivada }}
            </span>
          </div>
        </div>
      </section>

      <!-- Aparência -->
      <section class="review-card">
        <header class="review-card__header">
          <div class="review-card__heading">
            <span class="review-card__icon-wrap">
              <v-icon small>fas fa-desktop</v-icon>
            </span>
            <h4 class="review-card__title">Aparência</h4>
          </div>
          <FormButton
            text="Editar etapa"
            type="secundario"
            outlined
            before-icon="fas fa-pencil-alt"
            @click="goToStep(4)"
          />
        </header>
        <div class="review-fields">
          <div class="review-field">
            <span class="review-field__label">Aplicar em</span>
            <span class="review-field__value review-devices">
              <span v-if="selectedDevices.desktop" class="review-device-tag">
                <v-icon x-small>fas fa-desktop</v-icon>
                Desktop
              </span>
              <span v-if="selectedDevices.mobile" class="review-device-tag">
                <v-icon x-small>fas fa-mobile-alt</v-icon>
                Mobile
              </span>
            </span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Estilo de navegação</span>
            <span class="review-field__value">{{
              summary.aparencia.navegacao
            }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Ordenação na página</span>
            <span class="review-field__value">{{ ordemLabel }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Velocidade</span>
            <span class="review-field__value">{{
              summary.aparencia.velocidade
            }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Transição</span>
            <span class="review-field__value">{{
              summary.aparencia.transicao
            }}</span>
          </div>
        </div>
        <div class="review-fields review-fields--secondary">
          <div class="review-field">
            <span class="review-field__label">Exibição automática</span>
            <span class="review-field__value">{{
              summary.aparencia.autoplay
            }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Cor da navegação</span>
            <span class="review-field__value review-color">
              <span
                class="review-swatch"
                :style="{ backgroundColor: summary.aparencia.corFonte }"
              />
              {{ summary.aparencia.corFonte }}
            </span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Destaque centralizado</span>
            <span class="review-field__value">{{
              summary.aparencia.centerMode
            }}</span>
          </div>
          <div v-if="!productEditorMode" class="review-field">
            <span class="review-field__label">Fixar topo</span>
            <span class="review-field__value">{{
              summary.aparencia.fixedTop
            }}</span>
          </div>
          <div class="review-field">
            <span class="review-field__label">Exibir título</span>
            <span class="review-field__value">{{
              summary.aparencia.showTitle
            }}</span>
          </div>
          <div v-if="!productEditorMode" class="review-field">
            <span class="review-field__label">Banner principal</span>
            <span class="review-field__value">{{
              summary.aparencia.bannerPrincipal
            }}</span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import FormButton from "@/components/form/FormButton";
import { buildReviewSummary } from "../../utils/utilCarrossel";
import { mapGetters } from "vuex";

export default {
  name: "CarrosselStepRevisao",
  components: {
    FormButton
  },
  props: {
    wizardState: {
      type: Object,
      required: true
    },
    setoresFolha: {
      type: Array,
      default: () => []
    },
    segmentosCliente: {
      type: Array,
      default: () => []
    },
    paginasEstaticas: {
      type: Array,
      default: () => []
    },
    productEditorMode: {
      type: Boolean,
      default: false
    },
    carouselTypeVariant: {
      type: String,
      default: "type1"
    }
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    entityLabel() {
      return this.productEditorMode ? "vitrine" : "carrossel";
    },
    summary() {
      return buildReviewSummary(
        this.wizardState,
        this.setoresFolha,
        this.segmentosCliente,
        this.paginasEstaticas,
        { variant: this.carouselTypeVariant }
      );
    },
    bannersActionsLabel() {
      const slides = ((this.wizardState.carrossel || {}).slides || []).filter(
        slide => slide && slide.image
      );
      if (!slides.length) {
        return "Sem ação";
      }
      const withAction = slides.filter(slide => {
        const type = (slide.action && slide.action.type) || "nothing";
        return type && type !== "nothing";
      });
      if (!withAction.length) {
        return "Sem ação";
      }
      return `${withAction.length} configurada${
        withAction.length > 1 ? "s" : ""
      }`;
    },
    selectedDevices() {
      const selectedDevice =
        (this.wizardState.appearanceState &&
          this.wizardState.appearanceState.selectedDevice) ||
        {};
      return {
        desktop: !!selectedDevice.desktop,
        mobile: !!selectedDevice.mobile
      };
    },
    ordemLabel() {
      const appearanceState = this.wizardState.appearanceState || {};
      const ordem = appearanceState.ordem;
      if (!ordem) {
        return (
          (this.wizardState.carrossel &&
            this.wizardState.carrossel.ordem_area) ||
          "1"
        );
      }
      const parts = String(ordem).split(":");
      return parts[1] || parts[0] || "1";
    }
  },
  methods: {
    validate() {
      return true;
    },
    goToStep(step) {
      this.$emit("edit-step", step);
    }
  }
};
</script>

<style scoped>
.step-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: left;
}

.step-subtitle,
.review-publish-hint {
  text-align: left;
}

.step-subtitle {
  color: #666;
  margin-bottom: 8px;
}

.review-publish-hint {
  color: #888;
  font-size: 13px;
  margin: 0 0 16px;
  line-height: 1.4;
}

.review-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review-card {
  padding: 16px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  text-align: left;
}

.review-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.review-card__heading {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.review-card__icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(88, 112, 239, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color, #5870ef);
  flex-shrink: 0;
}

.review-card__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #333;
  text-align: left;
}

.review-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  text-align: left;
}

.review-fields--secondary {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;
}

.review-field {
  flex: 1;
  min-width: 140px;
  padding: 0 20px;
  border-right: 1px solid #eee;
  text-align: left;
}

.review-field:first-child {
  padding-left: 0;
}

.review-field:last-child {
  border-right: none;
}

.review-field--thumbs {
  flex: 1.5;
  min-width: 200px;
}

.review-field--programacao {
  flex: 1.6;
  min-width: 220px;
}

.review-programacao {
  list-style: none;
  margin: 0;
  padding: 0;
}

.review-programacao li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}

.review-programacao li:last-child {
  margin-bottom: 0;
}

.review-programacao__banner {
  font-size: 12px;
  color: #888;
}

.review-programacao__value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 1.35;
}

.review-field__label {
  display: block;
  font-size: 12px;
  color: #888;
  margin-bottom: 6px;
}

.review-field__value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 1.35;
  word-break: break-word;
}

.review-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f0f0f0;
  color: #555;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
}

.review-pill--muted {
  background: #f5f5f5;
  color: #666;
}

.review-pill--success {
  background: rgba(76, 175, 80, 0.12);
  color: #2e7d32;
}

.review-devices {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-weight: 500 !important;
}

.review-device-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #555;
}

.review-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
}

.review-thumb {
  width: 72px;
  height: 44px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.review-color {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.review-swatch {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid #ccc;
  display: inline-block;
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .review-field {
    flex: 1 1 100%;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
    padding: 12px 0;
  }

  .review-field:last-child {
    border-bottom: none;
  }
}

/* Dark mode */
.carrossel-step-revisao--dark .step-title,
.carrossel-step-revisao--dark .review-card__title,
.carrossel-step-revisao--dark .review-field__value,
.carrossel-step-revisao--dark .review-programacao__value {
  color: rgba(255, 255, 255, 0.87);
}

.carrossel-step-revisao--dark .step-subtitle,
.carrossel-step-revisao--dark .review-publish-hint,
.carrossel-step-revisao--dark .review-field__label,
.carrossel-step-revisao--dark .review-programacao__banner,
.carrossel-step-revisao--dark .review-device-tag {
  color: rgba(255, 255, 255, 0.65);
}

.carrossel-step-revisao--dark .review-card {
  background: var(--dark-surface, #1d1d1d);
  border-color: rgba(255, 255, 255, 0.12);
}

.carrossel-step-revisao--dark .review-card__header,
.carrossel-step-revisao--dark .review-fields--secondary {
  border-color: rgba(255, 255, 255, 0.08);
}

.carrossel-step-revisao--dark .review-field {
  border-color: rgba(255, 255, 255, 0.08);
}

.carrossel-step-revisao--dark .review-card__icon-wrap {
  background: rgba(88, 112, 239, 0.2);
}

.carrossel-step-revisao--dark .review-pill {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
}

.carrossel-step-revisao--dark .review-pill--success {
  background: rgba(76, 175, 80, 0.2);
  color: #81c784;
}

.carrossel-step-revisao--dark .review-thumb {
  border-color: rgba(255, 255, 255, 0.2);
}

.carrossel-step-revisao--dark .review-swatch {
  border-color: rgba(255, 255, 255, 0.25);
}
</style>
